import type { SailPlotExtensionContext } from "@heiselanalytics/sailplot";
import { platformVersion } from "../config/version";

const changelog = {
  en: {
    eyebrow: "Changelog",
    title: "What is new?",
    intro:
      "Release notes for the public SailPlot editor and its hosted platform pages.",
    releases: [
      {
        label: "Current release",
        version: platformVersion,
        released: "Released September 2026",
        releasedMachine: "2026-09",
        feedbackFriday: {
          label: "Feedback Friday integration",
          attribution: "From",
          contributors: ["Fabian Meier"],
        },
        entries: [
          "Added an Umpire boat class backed by the Coachboat hull geometry with a fixed grey hull; the obsolete Jury boat class was removed.",
          "Coachboats and Umpire boats now store and render independent boat-identification and umpire-signal flags. Identification colors are derived from racing boats in the active scenario; signal values are none, protest (Y), penalty (red), no penalty (green-white), yellow, blue, and disqualification (black).",
          "Sailing-boat classes now support a persistent three-state protest-flag position: left stern, off, or right stern. The red 3:2 flag and its visible pole are mirrored between the selected stern corners; support boats expose no stern protest-flag control.",
          "Animated exports now maintain a constant output frame rate at every Player speed, sampling additional interpolated positions for slower playback. GIF output increased from 10 to 30 FPS and MP4 from 24 to 30 FPS without changing sequence timing.",
          "Removed the Layline angle control from Scene settings; rendering retains the scenario value for backward compatibility with existing plots.",
        ],
      },
      {
        label: "Previous release",
        version: "0.10.0",
        released: "Released September 2026",
        releasedMachine: "2026-09",
        feedbackFriday: {
          label: "Feedback Friday integration",
          attribution: "From",
          contributors: ["Gauthier Thomas"],
        },
        entries: [
          "Added a dedicated Player layout: playback controls replace the left tool panel, the Properties panel is removed, and editor return/export actions remain pinned to the sidebar footer.",
          "Implemented a seekable normalized timeline. Every numbered position segment receives the same duration, and boats with shorter sequences hold their terminal position.",
          "Playback supports 0.5×, 1×, and 2× speed, optional travelled-path tails, persistent overlap indicators, and path-tangent hull rotation along curved routes.",
          "Updated the compact layout: language selection moved into the mobile menu, Player moved beside the menu trigger, and the Player close action uses a fixed red X without horizontal overflow.",
          "Grid intervals are computed as exactly 1 BL from the selected Boat-length basis. Displayed basis values are rounded to suppress floating-point artifacts.",
          "Boat-legend visibility now defaults to false and remains configurable in Scene settings.",
          "Changed boat rendering order so position numbers remain on the hull below sails; increased sail fill/outline contrast and reduced the rotation-handle stem length by 50%.",
          "Reworked shape hit testing: unfilled circles and rectangles accept pointer events only near their stroke, while filled shapes use their full area. Circles use one radial resize handle and click-created circles now terminate their creation state correctly.",
          "Added an unbounded viewport mode with pan, zoom down to 10%, Home reset, and freely positioned wind/legend overlays. Static and animated exports derive bounds from all scenario content and preserve legend margins; PDF is disabled because the unbounded canvas has no page geometry.",
          "Added Neutral as the default mark orientation and suppress laylines until Windward or Leeward is selected.",
          "Added looping GIF, transparent GIF, and MP4 generation to Player tools. Animation export reuses the active playback speed and tail state; the static export panel links to Player for dynamic formats.",
        ],
      },
      {
        label: "Earlier release",
        version: "0.9.0",
        released: "Released August 2026",
        releasedMachine: "2026-08",
        entries: [
          "Stable release with permanent two-step cancellation and electronic withdrawal declarations, immutable legal acceptance evidence, a controlled early-performance gate, and a reviewed privacy-retention workflow.",
          "Complete local-first sailing editor with boats, marks, gates, start and finish lines, drawing tools, text, and scene controls.",
          "Local projects, editable share URLs, QR codes, JSON backups, PNG exports, transparent PNG exports, and A4 PDF output.",
          "Responsive desktop and compact layouts with mouse, touch, stylus, and keyboard operation.",
          "German and English interface, detailed instructions, Open Source information, Legal Notice, and privacy policy.",
          "Hosted branding support for customer logos, colors, URLs, languages, links, and branded exports.",
        ],
      },
    ],
    note: "Future releases will be documented here with their version and publication month.",
  },
  de: {
    eyebrow: "Changelog",
    title: "Was ist neu?",
    intro:
      "Versionshinweise für den öffentlichen SailPlot-Editor und seine gehosteten Plattformseiten.",
    releases: [
      {
        label: "Aktuelle Version",
        version: platformVersion,
        released: "Veröffentlicht September 2026",
        releasedMachine: "2026-09",
        feedbackFriday: {
          label: "Feedback-Friday-Integration",
          attribution: "Von",
          contributors: ["Fabian Meier"],
        },
        entries: [
          "Eine neue Umpireboot-Klasse verwendet die Rumpfgeometrie des Coachboats mit festem grauem Rumpf; die veraltete Juryboot-Klasse wurde entfernt.",
          "Coachboats und Umpireboote speichern und rendern Bootskennflagge und Umpire-Signal unabhängig voneinander. Kennfarben stammen aus den Regattabooten des aktiven Szenarios; Signalwerte sind keine Flagge, Protest (Y), Strafe (Rot), keine Strafe (Grün-Weiß), Gelb, Blau und Disqualifikation (Schwarz).",
          "Segelbootklassen unterstützen jetzt eine persistente Protestflaggen-Position mit drei Zuständen: Heck links, aus oder Heck rechts. Die rote 3:2-Flagge samt sichtbarem Stock wird zwischen den gewählten Heckecken gespiegelt; Supportboote zeigen keine Heck-Protestflaggen-Steuerung.",
          "Animierte Exporte behalten bei jeder Player-Geschwindigkeit eine konstante Ausgabe-Framerate und berechnen für langsame Wiedergabe zusätzliche interpolierte Positionen. GIF wurde von 10 auf 30 FPS, MP4 von 24 auf 30 FPS erhöht, ohne das Sequenz-Timing zu verändern.",
          "Die Steuerung für den Layline-Winkel wurde aus den Szenen-Einstellungen entfernt; für die Abwärtskompatibilität bestehender Plots verwendet das Rendering weiterhin den gespeicherten Szenenwert.",
        ],
      },
      {
        label: "Vorherige Version",
        version: "0.10.0",
        released: "Veröffentlicht September 2026",
        releasedMachine: "2026-09",
        feedbackFriday: {
          label: "Feedback-Friday-Integration",
          attribution: "Von",
          contributors: ["Gauthier Thomas"],
        },
        entries: [
          "Ein dediziertes Player-Layout ersetzt links die Werkzeugleiste durch Wiedergabesteuerungen, entfernt die Eigenschaften-Seitenleiste und fixiert Editor-Rückkehr sowie Animationsexport am unteren Rand.",
          "Eine normalisierte, verschiebbare Zeitleiste weist jedem nummerierten Positionssegment dieselbe Dauer zu; Boote mit kürzeren Sequenzen halten ihre Endposition.",
          "Die Wiedergabe unterstützt 0,5×, 1× und 2×, optionale zurückgelegte Bootsspuren, persistente Overlap-Indikatoren und tangential zur gekrümmten Route ausgerichtete Rümpfe.",
          "Das Kompaktlayout verschiebt die Sprachauswahl in das mobile Menü und den Player neben den Menü-Trigger; die Player-Schliessaktion verwendet ein fixiertes rotes X ohne horizontalen Overflow.",
          "Rasterintervalle werden als exakt 1 BL aus der gewählten Boat-length basis berechnet. Angezeigte Basiswerte werden gerundet, um Gleitkomma-Artefakte zu unterdrücken.",
          "Die Sichtbarkeit der Bootslegende ist standardmässig false und bleibt in den Szenen-Einstellungen konfigurierbar.",
          "Die Renderreihenfolge hält Bootspositionsnummern auf dem Rumpf unter den Segeln; Segelfüllung und -konturen haben höheren Kontrast, der Stiel des Drehgriffs ist um 50 % verkürzt.",
          "Das Hit-Testing für Formen wurde überarbeitet: Ungefüllte Kreise und Rechtecke reagieren nur nahe ihrer Kontur, gefüllte Formen auf ihrer gesamten Fläche. Kreise verwenden einen radialen Resize-Griff; per Klick erzeugte Kreise verlassen den Erstellungszustand zuverlässig.",
          "Ein unbegrenzter Viewport unterstützt Verschieben, Zoom bis 10 %, Home-Reset und frei positionierbare Wind-/Legenden-Overlays. Statische und animierte Exporte berechnen ihre Grenzen aus allen Szeneninhalten und erhalten Legendenabstände; PDF ist ohne definierte Seitengeometrie deaktiviert.",
          "Neutral ist die neue Standardausrichtung für Tonnen; Laylines werden erst bei expliziter Auswahl von Luv oder Lee gerendert.",
          "Player-Werkzeuge erzeugen Loop-GIF, transparentes GIF und MP4 mit aktiver Wiedergabegeschwindigkeit und Bootsspurenstatus; der statische Exportbereich verlinkt für dynamische Formate in den Player.",
        ],
      },
      {
        label: "Frühere Version",
        version: "0.9.0",
        released: "Veröffentlicht August 2026",
        releasedMachine: "2026-08",
        entries: [
          "Stabile Version mit dauerhafter zweistufiger Kündigung, elektronischer Widerrufserklärung, unveränderlichen Rechtsnachweisen, kontrolliertem Leistungsbeginn und geprüftem Datenschutz-Löschprozess.",
          "Vollständiger lokal arbeitender Segeleditor mit Booten, Tonnen, Gates, Start- und Ziellinien, Zeichenwerkzeugen, Texten und Szenen-Einstellungen.",
          "Lokale Projekte, editierbare Freigabe-URLs, QR-Codes, JSON-Sicherungen, PNG-Exporte, transparente PNG-Exporte und A4-PDF-Ausgabe.",
          "Responsive Desktop- und Kompaktansichten mit Bedienung per Maus, Touch, Stift und Tastatur.",
          "Deutsche und englische Oberfläche, ausführliche Anleitung, Open-Source-Informationen, Impressum und Datenschutzerklärung.",
          "Gehostetes Branding für Kundenlogos, Farben, URLs, Sprachen, Links und personalisierte Exporte.",
        ],
      },
    ],
    note: "Künftige Veröffentlichungen werden hier mit Version und Veröffentlichungsmonat dokumentiert.",
  },
} as const;

interface ChangelogContentProps {
  language: SailPlotExtensionContext["language"];
  embedded?: boolean;
}

export function ChangelogContent({
  language,
  embedded = false,
}: ChangelogContentProps) {
  const text = changelog[language];
  const Title = embedded ? "h2" : "h1";
  const ReleaseTitle = embedded ? "h3" : "h2";

  return (
    <div
      className={`platform-changelog-content${embedded ? " platform-changelog-content--embedded" : ""}`}
    >
      <header className="platform-changelog-header">
        <span className="platform-page-eyebrow">{text.eyebrow}</span>
        <Title>{text.title}</Title>
        <p>{text.intro}</p>
      </header>

      {text.releases.map((release) => (
        <section className="platform-changelog-release" key={release.version}>
          <header>
            <div>
              <span>{release.label}</span>
              <ReleaseTitle>v{release.version}</ReleaseTitle>
            </div>
            <time dateTime={release.releasedMachine}>{release.released}</time>
          </header>
          {"feedbackFriday" in release && (
            <div className="platform-changelog-feedback-friday">
              <strong>{release.feedbackFriday.label}</strong>
              <span>
                {release.feedbackFriday.attribution}{" "}
                {release.feedbackFriday.contributors.join(", ")}
              </span>
            </div>
          )}
          <ul>
            {release.entries.map((entry) => (
              <li key={entry}>{entry}</li>
            ))}
          </ul>
        </section>
      ))}

      <p className="platform-changelog-note">{text.note}</p>
    </div>
  );
}
